/* Hair Assessment — Aurelian
   ------------------------------------------------------------------
   A separate engine from keep-my-hair-quiz.js and hair-assessment.js. Neither
   is imported, extended or modified; the flow model (declarative steps with
   hideIf branching, single-select auto-advance, the Keeps motion timings) is
   the same because the visual and interaction model must match.

   Fully client-side. No backend, no API keys, no third-party libraries.
   No PII is collected anywhere: no email, no name, no phone.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var root = document.getElementById('hair-assessment-aurelian');
  if (!root) return;

  var CFG = {};
  try { CFG = JSON.parse(root.querySelector('[data-haq-config]').textContent); } catch (e) { CFG = {}; }

  /* Motion, matched to the base quiz: leaving fades out over 300ms and the swap
     happens at 300ms; entering slides 40px from the right over 500ms after 50ms. */
  var LEAVE_MS = 300, ENTER_MS = 500, ENTER_DELAY = 50;

  /* One line to change if the cutoff moves to include NW5. */
  var DISQUALIFY_STAGES = ['nw6', 'nw7'];

  var SS_KEY = 'alfavita_haq_v1';          /* new key — does not collide with       */
  var LS_KEY = 'alfavita_haq_result_v1';   /* alfavita_hair_quiz_v1 or the kq:* set */

  /* ---------- flow ----------------------------------------------------
     `field` is the answer key. Steps without one are interstitials.
     `noCount` keeps a step out of the progress denominator: 10a shares step
     10's value so the bar neither jumps back nor gains a visible step. */
  var STEPS = [
    { id: 'goal',           field: 'goal' },
    { id: 'description',    field: 'description' },
    { id: 'stage',          field: 'stage' },
    { id: 'disqualify',     terminal: true,
      hideIf: function (a) { return DISQUALIFY_STAGES.indexOf(a.stage) === -1; } },
    { id: 'duration',       field: 'duration' },
    { id: 'noticed',        field: 'noticed' },
    { id: 'treated_before', field: 'treated_before' },
    { id: 'prior_outcome',  field: 'prior_outcome',
      hideIf: function (a) { return a.treated_before !== 'yes'; } },
    { id: 'reviews' },
    { id: 'regimen',        field: 'regimen' },
    { id: 'evidence',       noCount: true,
      hideIf: function (a) { return a.regimen !== 'minoxidil' && a.regimen !== 'both'; } },
    { id: 'priorities' },
    { id: 'belief' },
    { id: 'read' },
    { id: 'processing',     loader: { from: 0, to: 100, ms: 4000 } },
    { id: 'gate' },
    { id: 'reserving',      loader: { from: 0, to: 100, ms: 3000, last: true } },
    { id: 'payoff',         terminal: true }
  ];

  /* Everything past the disqualification screen is unreachable from it. */
  STEPS.forEach(function (s, i) {
    if (s.id === 'disqualify') s._dqIndex = i;
  });

  /* ---------- state ---------- */
  function blank() {
    return {
      v: 1,
      goal: null,
      description: null,
      stage: null,
      duration: null,
      noticed: null,
      treated_before: null,
      prior_outcome: null,
      regimen: null,
      priorities: [],
      confirmed: false,
      completed_at: null
    };
  }
  var state = blank();
  var index = 0;
  var busy = false;
  var booted = false;

  function persist() {
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify({ s: state, i: index }));
    } catch (e) { /* private mode — quiz still runs, just no refresh persistence */ }
  }
  function restore() {
    var raw;
    try { raw = sessionStorage.getItem(SS_KEY); } catch (e) { return; }
    if (!raw) return;
    var parsed;
    try { parsed = JSON.parse(raw); } catch (e) { return; }
    if (!parsed || !parsed.s || parsed.s.v !== 1) return;
    var s = parsed.s;
    Object.keys(state).forEach(function (k) { if (s[k] !== undefined) state[k] = s[k]; });
    if (!Array.isArray(state.priorities)) state.priorities = [];
    var i = parseInt(parsed.i, 10);
    if (isNaN(i) || i < 0 || i >= STEPS.length) return;
    /* Never resume onto a loader: its timer fires on arrival and would throw
       the visitor forward before they saw anything. Walk back to a real step. */
    while (i > 0 && (STEPS[i].loader || hidden(STEPS[i]))) i--;
    index = i;
  }

  /* ---------- branching ---------- */
  function hidden(step) {
    return typeof step.hideIf === 'function' ? !!step.hideIf(state) : false;
  }
  function visibleSteps() {
    return STEPS.filter(function (s) { return !hidden(s) && !s.noCount; });
  }
  function nextIndex(from) {
    var i = from + 1;
    while (i < STEPS.length && hidden(STEPS[i])) i++;
    return i;
  }
  function prevIndex(from) {
    var i = from - 1;
    while (i >= 0 && hidden(STEPS[i])) i--;
    return i;
  }

  /* ---------- DOM ---------- */
  var slides = root.querySelector('[data-haq-slides]');
  var backBtn = root.querySelector('[data-haq-back]');
  var bar = root.querySelector('[data-haq-progress]');
  var countEl = root.querySelector('[data-haq-count]');
  var stepEls = {};
  Array.prototype.forEach.call(root.querySelectorAll('[data-haq-step]'), function (el) {
    stepEls[el.getAttribute('data-haq-step')] = el;
  });
  function currentEl() { return stepEls[STEPS[index].id]; }

  /* ---------- labels ---------- */
  var DURATION_LABELS = {
    none: 'Preventing — no change yet',
    lt6: 'Under 6 months',
    '6to12': '6–12 months',
    '1to3': '1–3 years',
    gt3: 'More than 3 years'
  };
  var GOAL_LABELS = { prevent: 'Prevent', keep: 'Keep', recover: 'Recover' };
  var REGIMEN_LABELS = { minoxidil: 'Minoxidil', both: 'Minoxidil', dht_oral: 'Finasteride or dutasteride', none: 'Nothing right now' };
  /* Read off the rendered button so the wording stays with the locale file. */
  function stageLabel() {
    var btn = root.querySelector('[data-haq-value="' + state.stage + '"]');
    return btn ? btn.getAttribute('data-label') : (state.stage || '—');
  }
  function onMinoxidil() { return state.regimen === 'minoxidil' || state.regimen === 'both'; }

  /* ---------- analytics ----------------------------------------------
     Nothing in this theme fires dataLayer/gtag/fbq, and the store's tooling
     runs inside Shopify's Web Pixels sandbox, which cannot see theme-side
     dataLayer pushes. Shopify.analytics.publish is the supported bridge from
     theme code into that sandbox, so every event goes there; the others are
     called only if something ever defines them. Prefixed haq_ so these never
     mix with the existing quiz's numbers. */
  function track(name, payload) {
    var evt = 'haq_' + name;
    var data = payload || {};
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(evt, data);
      }
    } catch (e) {}
    try { if (window.dataLayer && window.dataLayer.push) window.dataLayer.push(Object.assign({ event: evt }, data)); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', evt, data); } catch (e) {}
    try { if (typeof window.fbq === 'function') window.fbq('trackCustom', evt, data); } catch (e) {}
    try { if (typeof window.clarity === 'function') window.clarity('event', evt); } catch (e) {}
  }

  /* Optional results POST. Disabled unless a section setting supplies a URL, so
     with no URL the quiz stays entirely inside Shopify as specified. Fired and
     forgotten: a logging failure must never cost the sale. */
  function logResult() {
    if (!CFG.endpoint) return;
    try {
      var body = JSON.stringify(state);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(CFG.endpoint, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(CFG.endpoint, { method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true })
          .catch(function () {});
      }
    } catch (e) {}
  }

  /* ---------- render ---------- */
  function paintSelection(el) {
    var field = STEPS[index].field;
    if (!field) return;
    var chosen = state[field];
    Array.prototype.forEach.call(el.querySelectorAll('[data-haq-value]'), function (b) {
      b.classList.toggle('is-selected', b.getAttribute('data-haq-value') === chosen);
    });
  }

  function paintChrome() {
    var step = STEPS[index];
    var vis = visibleSteps();
    /* 10a is noCount: it borrows the position of the step before it, so the bar
       holds still rather than jumping back or growing the denominator. */
    var ref = step.noCount ? STEPS[prevIndex(index)] : step;
    var pos = vis.indexOf(ref) + 1;
    var total = vis.length;
    /* A terminal screen is the end of this visitor's path whatever its position
       in the array, so the bar reads full rather than stopping a quarter of the
       way along on the disqualification screen. */
    var frac = step.terminal ? 1 : (total ? pos / total : 0);
    if (bar) bar.style.transform = 'scaleX(' + frac.toFixed(4) + ')';
    if (countEl) countEl.textContent = step.terminal ? '' : 'Step ' + pos + ' of ' + total;
    if (backBtn) backBtn.hidden = index === 0 || !!step.terminal;
  }

  function animateIn() {
    slides.classList.remove('is-leaving', 'is-entered');
    slides.classList.add('is-entering');
    void slides.offsetWidth;                    /* force reflow before the transition */
    slides.classList.remove('is-entering');
    slides.classList.add('is-entered');
    window.setTimeout(function () { slides.style.willChange = 'auto'; }, ENTER_MS + ENTER_DELAY + 50);
  }

  function render() {
    var step = STEPS[index];
    var el = currentEl();
    if (!el) return;
    Object.keys(stepEls).forEach(function (k) { stepEls[k].hidden = true; });
    el.hidden = false;
    paintSelection(el);
    paintChrome();
    animateIn();

    if (step.id === 'priorities') paintRanks();
    if (step.id === 'read') fillRead();
    if (step.id === 'payoff') fillPayoff();
    if (step.id === 'gate') paintGate();
    if (step.id === 'reviews') syncRailNav();
    if (step.loader) runLoader(el, step.loader);

    if (step.id === 'disqualify') track('disqualified', { stage: state.stage });
    if (step.id === 'evidence') track('banner_shown', { regimen: state.regimen });
    track('step_view', { step: visibleSteps().indexOf(step) + 1, step_name: step.id });

    if (booted) root.scrollIntoView({ block: 'start', behavior: 'smooth' });
    booted = true;
    persist();
  }

  /* ---------- navigation ----------
     Position lives in history state so the browser back button behaves exactly
     like the in-quiz arrow instead of leaving the page. Branch-aware in both
     directions because prevIndex/nextIndex skip hidden steps: back from
     "reviews" lands on 8 when they answered Yes and on 7 when they answered No,
     without any bookkeeping. */
  function pushHistory(replace) {
    var url = location.pathname + '?step=' + (index + 1) + location.hash;
    try {
      history[replace ? 'replaceState' : 'pushState']({ haq: index }, '', url);
    } catch (e) {}
  }

  function goTo(nextIdx, opts) {
    opts = opts || {};
    if (busy) return;
    if (nextIdx >= STEPS.length) return;
    busy = true;
    slides.classList.remove('is-entered', 'is-entering');
    slides.classList.add('is-leaving');
    window.setTimeout(function () {
      index = nextIdx;
      busy = false;
      if (!opts.fromHistory) pushHistory(false);
      render();
    }, LEAVE_MS);
  }

  function advance() { goTo(nextIndex(index)); }
  function back() {
    if (busy || index === 0) return;
    /* Let the browser drive so its stack and ours cannot disagree. */
    try { history.back(); } catch (e) { goTo(prevIndex(index), { fromHistory: true }); }
  }

  window.addEventListener('popstate', function (ev) {
    var target = ev.state && typeof ev.state.haq === 'number' ? ev.state.haq : 0;
    if (target === index) return;
    if (target < 0 || target >= STEPS.length) return;
    if (hidden(STEPS[target])) target = prevIndex(target);
    goTo(target, { fromHistory: true });
  });

  /* ---------- step 11: ranked multi-select ---------- */
  var MAX_RANKS = 3;
  function rankGroup() { return root.querySelector('[data-haq-rank-group]'); }
  function paintRanks() {
    var group = rankGroup();
    if (!group) return;
    Array.prototype.forEach.call(group.querySelectorAll('[data-haq-rank]'), function (btn) {
      var key = btn.getAttribute('data-haq-rank');
      var at = state.priorities.indexOf(key);
      var badge = btn.querySelector('[data-haq-rank-badge]');
      btn.classList.toggle('is-ranked', at > -1);
      btn.setAttribute('aria-pressed', at > -1 ? 'true' : 'false');
      if (badge) badge.textContent = at > -1 ? String(at + 1) : '';
    });
    group.classList.toggle('is-full', state.priorities.length >= MAX_RANKS);
    var cont = root.querySelector('[data-haq-rank-continue]');
    /* Appears on the FIRST selection — three are never forced. */
    if (cont) cont.hidden = state.priorities.length === 0;
  }
  function toggleRank(key) {
    var at = state.priorities.indexOf(key);
    if (at > -1) {
      /* Removing renumbers the rest: splice keeps the array contiguous, which
         is the whole contract of this screen. */
      state.priorities.splice(at, 1);
    } else if (state.priorities.length < MAX_RANKS) {
      state.priorities.push(key);
    } else {
      return;                                  /* fourth click: silently nothing */
    }
    persist();
    paintRanks();
  }

  /* ---------- step 13: the read ---------- */
  function fillRead() {
    var el = stepEls.read;
    if (!el) return;
    var set = function (k, v) {
      var n = el.querySelector('[data-haq-read="' + k + '"]');
      if (n) n.textContent = v;
    };
    set('stage', stageLabel());
    set('goal', GOAL_LABELS[state.goal] || '—');

    /* "unsure" prints nothing at all rather than echoing "Hard to say" back. */
    var durRow = el.querySelector('[data-haq-read-row="duration"]');
    if (durRow) durRow.hidden = !state.duration || state.duration === 'unsure';
    if (state.duration && state.duration !== 'unsure') set('duration', DURATION_LABELS[state.duration] || '—');

    var regRow = el.querySelector('[data-haq-read-row="regimen"]');
    if (regRow) regRow.hidden = !onMinoxidil();
    if (onMinoxidil()) set('regimen', 'Minoxidil');

    var combo = el.querySelector('[data-haq-read-row="combination"]');
    if (combo) combo.hidden = !onMinoxidil();
  }

  /* ---------- step 17: the payoff ---------- */
  function fillPayoff() {
    var el = stepEls.payoff;
    if (!el) return;
    var read = stageLabel();
    if (state.duration && state.duration !== 'unsure') {
      read += state.duration === 'none'
        ? ' · preventing, no change yet'
        : ' · changing ' + (DURATION_LABELS[state.duration] || '').toLowerCase();
    }
    if (onMinoxidil()) read += ' · alongside minoxidil';
    var r = el.querySelector('[data-haq-payoff="read"]');
    if (r) r.textContent = read;
    var s = el.querySelector('[data-haq-payoff="schedule"]');
    if (s) {
      s.textContent = onMinoxidil()
        ? 'Two minutes, once a week. Minoxidil pauses for 24 hours afterwards.'
        : 'Two minutes, once a week.';
    }
  }

  /* ---------- step 15: the gate ---------- */
  function gateInputs() { return Array.prototype.slice.call(root.querySelectorAll('[data-haq-gate]')); }
  function paintGate() {
    gateInputs().forEach(function (input) {
      var item = input.closest('[data-haq-gate-item]');
      if (item) item.classList.toggle('is-checked', input.checked);
    });
    var all = gateInputs().every(function (i) { return i.checked; });
    var btn = root.querySelector('[data-haq-gate-submit]');
    if (btn) btn.disabled = !all;
    state.confirmed = all;
  }

  /* ---------- step 9: rail arrows ---------- */
  function rail() { return root.querySelector('[data-haq-rail]'); }
  function syncRailNav() {
    var el = rail();
    if (!el) return;
    var prev = root.querySelector('[data-haq-rail-prev]');
    var next = root.querySelector('[data-haq-rail-next]');
    var max = el.scrollWidth - el.clientWidth;
    if (prev) prev.hidden = el.scrollLeft <= 4;
    if (next) next.hidden = el.scrollLeft >= max - 4;
  }
  function scrollRail(dir) {
    var el = rail();
    if (!el) return;
    var card = el.firstElementChild;
    var pitch = card ? card.getBoundingClientRect().width + 16 : 280;
    el.scrollBy({ left: dir * pitch, behavior: 'smooth' });
  }

  /* ---------- loaders ---------- */
  var loaderTimer = null;
  function runLoader(el, spec) {
    window.clearInterval(loaderTimer);
    var pct = el.querySelector('[data-haq-pct]');
    var items = el.querySelectorAll('.haq-loader-item');
    var t0 = null;
    var span = spec.to - spec.from;
    loaderTimer = window.setInterval(function () {
      if (t0 === null) t0 = Date.now();
      var p = Math.min(1, (Date.now() - t0) / spec.ms);
      if (pct) pct.textContent = Math.round(spec.from + span * p) + '%';
      var active = Math.min(items.length - 1, Math.floor(p * items.length));
      Array.prototype.forEach.call(items, function (li, i) {
        li.classList.toggle('is-done', i < active || p === 1);
        li.classList.toggle('is-active', i === active && p < 1);
      });
      if (p === 1) {
        window.clearInterval(loaderTimer);
        window.setTimeout(function () { advance(); }, 420);
      }
    }, 60);
  }

  /* ---------- exit ---------- */
  function finish() {
    state.completed_at = new Date().toISOString();
    persist();
    track('complete', state);
    logResult();
    try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch (e) {}
    try { sessionStorage.removeItem(SS_KEY); } catch (e) {}

    var base = CFG.pdpUrl || '/pages/aurelian-protocol';
    /* Inbound UTMs are carried through untouched — the quiz only adds its own
       keys, and only where the incoming URL has not already set them. */
    var incoming = new URLSearchParams(location.search);
    incoming.delete('step');
    var parts = base.split('#');
    var hash = parts[1] ? '#' + parts[1] : '';
    var path = parts[0].split('?');
    var out = new URLSearchParams(path[1] || '');
    incoming.forEach(function (v, k) { if (!out.has(k)) out.set(k, v); });
    out.set('quiz', 'aurelian');
    if (state.stage) out.set('stage', state.stage);
    if (state.goal) out.set('goal', state.goal);
    if (state.priorities[0]) out.set('priority', state.priorities[0]);
    window.location.href = path[0] + '?' + out.toString() + hash;
  }

  /* ---------- interactions ---------- */
  root.addEventListener('click', function (ev) {
    var t = ev.target;

    var rank = t.closest && t.closest('[data-haq-rank]');
    if (rank && root.contains(rank)) { ev.preventDefault(); toggleRank(rank.getAttribute('data-haq-rank')); return; }

    var opt = t.closest && t.closest('[data-haq-value]');
    if (opt && root.contains(opt)) {
      if (busy) return;
      var field = STEPS[index].field;
      if (field) {
        state[field] = opt.getAttribute('data-haq-value');
        /* Answering again after coming back invalidates a branch answer that no
           longer applies, so a stale "how did that go" cannot survive a switch
           to No. */
        if (field === 'treated_before' && state[field] !== 'yes') state.prior_outcome = null;
        persist();
        paintSelection(currentEl());   /* the selected state flashes during the fade */
        track('step_answer', { step: visibleSteps().indexOf(STEPS[index]) + 1, step_name: STEPS[index].id, value: state[field] });
      }
      advance();
      return;
    }

    var skip = t.closest && t.closest('[data-haq-skip]');
    if (skip && root.contains(skip)) {
      if (busy) return;
      var f = STEPS[index].field;
      var v = skip.getAttribute('data-haq-skip');
      if (f && v) {
        state[f] = v;
        persist();
        track('step_answer', { step: visibleSteps().indexOf(STEPS[index]) + 1, step_name: STEPS[index].id, value: v });
      }
      advance();
      return;
    }

    if (t.closest && t.closest('[data-haq-gate-submit]')) {
      ev.preventDefault();
      if (!gateInputs().every(function (i) { return i.checked; })) return;
      state.confirmed = true;
      persist();
      track('gate_confirmed', {});
      advance();
      return;
    }
    if (t.closest && t.closest('[data-haq-finish]'))    { ev.preventDefault(); finish(); return; }
    if (t.closest && t.closest('[data-haq-rail-prev]')) { ev.preventDefault(); scrollRail(-1); return; }
    if (t.closest && t.closest('[data-haq-rail-next]')) { ev.preventDefault(); scrollRail(1); return; }
    if (t.closest && t.closest('[data-haq-back]'))      { ev.preventDefault(); back(); return; }
    if (t.closest && t.closest('[data-haq-next]'))      {
      ev.preventDefault();
      if (STEPS[index].id === 'priorities') {
        if (!state.priorities.length) return;
        track('priorities_ranked', { priorities: state.priorities.slice() });
      }
      advance();
      return;
    }
  });

  root.addEventListener('change', function (ev) {
    if (ev.target.closest && ev.target.closest('[data-haq-gate]')) paintGate();
  });

  var railEl = rail();
  if (railEl) railEl.addEventListener('scroll', syncRailNav, { passive: true });

  /* ---------- boot ---------- */
  restore();

  /* A deep link with no state cannot render a meaningful screen, so it starts
     at step 1 rather than a half-populated summary. */
  var wanted = parseInt(new URLSearchParams(location.search).get('step'), 10);
  if (!isNaN(wanted)) {
    var w = wanted - 1;
    if (w > 0 && (!state.goal || w >= STEPS.length || hidden(STEPS[w]))) w = 0;
    if (w >= 0 && w < STEPS.length && state.goal) index = Math.min(index, Math.max(w, 0));
    if (!state.goal) index = 0;
  }
  while (index > 0 && (STEPS[index].loader || hidden(STEPS[index]))) index--;

  pushHistory(true);
  /* before render(), so the session opens with quiz_start rather than with the
     step_view that render() fires for screen one */
  track('start', {});
  render();
})();
